/**
 * migrations.ts — DemoState version migrations.
 *
 * migrateDemoState() is called on every load after reading from localStorage.
 * It upgrades the state to the current schema version.
 *
 * Before a destructive recovery, a backup is created automatically.
 *
 * Compatibility:
 *   muni-001 → Berga
 *   muni-002 → Manresa
 *   muni-003 → Vic
 *
 * Schema versions:
 *   v1 → initial PR#18 schema (workspaceId on sessions/issues/drafts)
 *   v2 → PR#18 QA: workspaceId backfill on notifications/alerts;
 *         re-migration of any pre-PR18 state that reached v1 without proper backfill
 *   v3 → PR#19: remove legacy ws-muni-001-general seed workspace so Berga General
 *         behaves like any other municipality (shows "Crea espai de demostració").
 *         Only removes the identifiable seed workspace — user-created General workspaces
 *         for Berga are preserved.
 *         Normalises legacy demo profile (Montserrat Vidal → Marta Serra).
 */

import type { DemoState, DemoIssue, DemoNotification } from './schema';
import { CURRENT_VERSION, CURRENT_SCHEMA_VERSION } from './schema';
import { createInitialDemoState } from './initialState';
import { backupCurrentState } from './storage';

// ─── Helpers ───────────────────────────────────────────────────────────

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function ensureArray<T>(v: unknown, fallback: T[] = []): T[] {
  return Array.isArray(v) ? (v as T[]) : fallback;
}

function ensureString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function ensureBoolean(v: unknown, fallback = false): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

// ─── Workspace mapping for legacy backfill ─────────────────────────────

/**
 * Maps municipiId → default workspaceId for legacy entities without workspaceId.
 *
 * For muni-001 (Berga), we default to 'ws-muni-001-general' because:
 *  - Pre-PR18 data was created in a context without org separation
 *  - General is the neutral/demo workspace — safest assignment
 *  - AC workspace (ws-muni-001-ac) has its own seed data with explicit IDs
 *  - This prevents legacy Berga data from appearing in Berga AC
 *
 * Manresa (muni-002) and Vic (muni-003) belong exclusively to AC.
 */
const LEGACY_WORKSPACE_MAP: Record<string, string> = {
  'muni-001': 'ws-muni-001-general',
  'muni-002': 'ws-muni-002',
  'muni-003': 'ws-muni-003',
};

function backfillWorkspaceId<T extends { municipiId?: string; workspaceId?: string | null }>(
  items: T[]
): T[] {
  return items.map((item) => {
    if (item.workspaceId) return item; // already has workspaceId
    const wsId = item.municipiId ? LEGACY_WORKSPACE_MAP[item.municipiId] ?? null : null;
    return { ...item, workspaceId: wsId };
  });
}

/**
 * Backfill workspaceId on notifications/alerts using municipalityId.
 * Notifications without workspaceId are assigned to the General workspace
 * for muni-001, or the single workspace for muni-002/003.
 */
function backfillNotificationWorkspaceId(items: DemoNotification[]): DemoNotification[] {
  return items.map((item) => {
    if (item.workspaceId) return item;
    const wsId = item.municipalityId ? LEGACY_WORKSPACE_MAP[item.municipalityId] ?? null : null;
    return { ...item, workspaceId: wsId };
  });
}

// ─── Migration: any version → v1/schemaV2 ─────────────────────────────

function migrateToV1SchemaV2(raw: Record<string, unknown>): DemoState {
  const seed = createInitialDemoState();

  // Merge persisted arrays if they exist, otherwise use seed
  const issues: DemoIssue[] = ensureArray<DemoIssue>(raw['issues'], seed.issues).map((issue) => {
    // Fix: status === 'arxivat' but archivedAt is null/undefined
    if (issue.status === 'arxivat' && !issue.archivedAt) {
      return {
        ...issue,
        archivedAt: new Date().toISOString(),
        statusBeforeArchive: (issue as DemoIssue).statusBeforeArchive ?? 'en_estudi',
      };
    }
    return {
      ...issue,
      archivedAt: issue.archivedAt ?? null,
      deletedAt: issue.deletedAt ?? null,
      deletedBy: issue.deletedBy ?? null,
      statusBeforeArchive: (issue as DemoIssue).statusBeforeArchive ?? null,
    };
  });

  // Backfill workspaceId on legacy entities that lack it
  const migratedSessions = backfillWorkspaceId(
    ensureArray(raw['plenarySessions'], seed.plenarySessions)
  );
  const migratedIssues = backfillWorkspaceId(issues);
  const migratedDrafts = backfillWorkspaceId(
    ensureArray(raw['drafts'], seed.drafts).map((d) => ({
      ...d,
      archivedAt: (d as unknown as Record<string, unknown>)['archivedAt'] as string | null ?? null,
      deletedAt: (d as unknown as Record<string, unknown>)['deletedAt'] as string | null ?? null,
      deletedBy: (d as unknown as Record<string, unknown>)['deletedBy'] as string | null ?? null,
      presentedAt: (d as unknown as Record<string, unknown>)['presentedAt'] as string | null ?? null,
      respondedAt: (d as unknown as Record<string, unknown>)['respondedAt'] as string | null ?? null,
    }))
  );

  // Backfill workspaceId on notifications and alerts (new in schemaV2)
  const rawAlerts = ensureArray<DemoNotification>(raw['alerts'], seed.alerts);
  const migratedAlerts = backfillNotificationWorkspaceId(rawAlerts);

  const rawNotifications = ensureArray<DemoNotification>(raw['notifications'], seed.notifications);
  const migratedNotifications = backfillNotificationWorkspaceId(rawNotifications);

  // Merge organizationMunicipalityConfigs: preserve persisted + add any missing seed configs
  const persistedConfigs = ensureArray(raw['organizationMunicipalityConfigs'], []);
  const persistedConfigIds = new Set(persistedConfigs.map((c: unknown) => (c as Record<string, unknown>)['id'] as string));
  const mergedConfigs = [
    ...persistedConfigs,
    ...seed.organizationMunicipalityConfigs.filter((c) => !persistedConfigIds.has(c.id)),
  ];

  return {
    ...seed,
    version: CURRENT_VERSION,
    schemaVersion: CURRENT_SCHEMA_VERSION,

    // Preserve persisted user data
    userProfiles: ensureArray(raw['userProfiles'], seed.userProfiles),
    organizationMemberships: ensureArray(raw['organizationMemberships'], seed.organizationMemberships),
    workspaceMemberships: ensureArray(raw['workspaceMemberships'], seed.workspaceMemberships),

    workspaces: ensureArray(raw['workspaces'], seed.workspaces),
    activeWorkspaceId: typeof raw['activeWorkspaceId'] === 'string' ? raw['activeWorkspaceId'] : null,
    activeOrganizationId: typeof raw['activeOrganizationId'] === 'string' ? raw['activeOrganizationId'] : null,

    organizationMunicipalityConfigs: mergedConfigs,

    plenarySessions: migratedSessions,
    agendaItems: ensureArray(raw['agendaItems'], seed.agendaItems),
    documents: ensureArray(raw['documents'], seed.documents),
    citations: ensureArray(raw['citations'], []),

    issues: migratedIssues,

    drafts: migratedDrafts,

    suggestedQuestions: ensureArray(raw['suggestedQuestions'], seed.suggestedQuestions).map((q) => ({
      ...q,
      dismissedAt: (q as unknown as Record<string, unknown>)['dismissedAt'] as string | null ?? null,
    })),

    internalNotes: isObject(raw['internalNotes']) ? (raw['internalNotes'] as Record<string, string[]>) : {},
    itemResponsables: isObject(raw['itemResponsables']) ? (raw['itemResponsables'] as Record<string, string>) : {},

    activityEvents: ensureArray(raw['activityEvents'], seed.activityEvents),
    alerts: migratedAlerts,
    notifications: migratedNotifications,

    preferences: isObject(raw['preferences'])
      ? {
          theme: ensureString((raw['preferences'] as Record<string, unknown>)['theme'], 'clar') as 'clar' | 'fosc' | 'sistema',
          notifNouDocument: ensureBoolean((raw['preferences'] as Record<string, unknown>)['notifNouDocument'], true),
          notifTerminis: ensureBoolean((raw['preferences'] as Record<string, unknown>)['notifTerminis'], true),
          notifEsborranys: ensureBoolean((raw['preferences'] as Record<string, unknown>)['notifEsborranys'], false),
          notifAssumptes: ensureBoolean((raw['preferences'] as Record<string, unknown>)['notifAssumptes'], true),
        }
      : seed.preferences,

    metadata: {
      createdAt: typeof raw['metadata'] === 'object' && raw['metadata'] !== null
        ? ensureString((raw['metadata'] as Record<string, unknown>)['createdAt'], seed.metadata.createdAt)
        : seed.metadata.createdAt,
      lastModifiedAt: new Date().toISOString(),
      seedVersion: '2.1.0',
    },
  };
}

// ─── Migration: schemaV2 → schemaV3 (PR#19) ───────────────────────────

/**
 * Applies PR#19 changes on top of an already-migrated schemaV2 state:
 *
 * 1. Removes the legacy seed workspace ws-muni-001-general so Berga General
 *    behaves like any other municipality (shows "Crea espai de demostració").
 *    ONLY removes the identifiable seed workspace — any workspace for muni-001
 *    in org-plenaricat-general that was created AFTER the seed (i.e. not the
 *    original seed entry) is preserved.
 *    Identification: id === 'ws-muni-001-general' AND organizationId === 'org-plenaricat-general'
 *
 * 2. Removes the corresponding workspace membership (wmem-001b).
 *
 * 3. If activeWorkspaceId pointed to ws-muni-001-general, clears it so the
 *    user is redirected to /espais on next load.
 *
 * 4. Removes ALL operational entities associated with ws-muni-001-general:
 *    PlenarySession, AgendaItem, Document, Issue, Draft, SuggestedQuestion,
 *    ActivityEvent, Alerts, Notifications.
 *    ONLY affects ws-muni-001-general — ws-muni-001-ac and all other workspaces
 *    are untouched.
 *
 * 5. Cleans up internalNotes: removes note entries whose key references an
 *    entityId that was deleted during this cleanup. Does NOT do a global reset.
 *
 * 6. Normalises legacy demo profile: if a userProfile has nom 'Montserrat' *    and cognoms containing'Vidal', updates it to Marta Serra / demo@plenari.cat.
 */
function applySchemaV3(state: DemoState): DemoState {
  const LEGACY_SEED_WS_ID = 'ws-muni-001-general';
  const LEGACY_SEED_ORG_ID = 'org-plenaricat-general';

  // 1. Remove legacy seed workspace (only the exact seed entry)
  const workspaces = state.workspaces.filter(
    (w) => !(w.id === LEGACY_SEED_WS_ID && w.organizationId === LEGACY_SEED_ORG_ID)
  );

  // 2. Remove corresponding workspace membership
  const workspaceMemberships = state.workspaceMemberships.filter(
    (m) => m.workspaceId !== LEGACY_SEED_WS_ID
  );

  // 3. Clear activeWorkspaceId if it pointed to the removed workspace
  const activeWorkspaceId =
    state.activeWorkspaceId === LEGACY_SEED_WS_ID ? null : state.activeWorkspaceId;

  // 4. Remove ALL operational entities associated with ws-muni-001-general
  //    ONLY this workspace — ws-muni-001-ac and all others are untouched.

  const plenarySessions = state.plenarySessions.filter(
    (s) => s.workspaceId !== LEGACY_SEED_WS_ID
  );

  // Collect removed session IDs to cascade-clean agendaItems and documents
  const removedSessionIds = new Set(
    state.plenarySessions
      .filter((s) => s.workspaceId === LEGACY_SEED_WS_ID)
      .map((s) => s.id)
  );

  const agendaItems = state.agendaItems.filter(
    (item) =>
      item.workspaceId !== LEGACY_SEED_WS_ID &&
      !removedSessionIds.has(item.plenarySessionId)
  );

  // Collect removed agendaItem IDs for suggestedQuestions cascade
  const removedAgendaItemIds = new Set(
    state.agendaItems
      .filter(
        (item) =>
          item.workspaceId === LEGACY_SEED_WS_ID ||
          removedSessionIds.has(item.plenarySessionId)
      )
      .map((item) => item.id)
  );

  const documents = state.documents.filter(
    (doc) =>
      doc.workspaceId !== LEGACY_SEED_WS_ID &&
      !removedSessionIds.has(doc.plenarySessionId ?? '')
  );

  const issues = state.issues.filter(
    (issue) => issue.workspaceId !== LEGACY_SEED_WS_ID
  );

  // Collect removed issue IDs for notes cascade
  const removedIssueIds = new Set(
    state.issues
      .filter((issue) => issue.workspaceId === LEGACY_SEED_WS_ID)
      .map((issue) => issue.id)
  );

  const drafts = state.drafts.filter(
    (draft) => draft.workspaceId !== LEGACY_SEED_WS_ID
  );

  // Collect removed draft IDs for notes cascade
  const removedDraftIds = new Set(
    state.drafts
      .filter((draft) => draft.workspaceId === LEGACY_SEED_WS_ID)
      .map((draft) => draft.id)
  );

  // SuggestedQuestions: remove those referencing removed agendaItems
  const suggestedQuestions = state.suggestedQuestions.filter(
    (sq) => !removedAgendaItemIds.has(sq.agendaItemId)
  );

  const activityEvents = state.activityEvents.filter(
    (ev) => ev.workspaceId !== LEGACY_SEED_WS_ID
  );

  // Alerts and notifications: remove those associated with ws-muni-001-general.
  // Primary boundary: workspaceId. Secondary: entityId references deleted entities.
  const removedEntityIds = new Set([
    ...removedSessionIds,
    ...removedAgendaItemIds,
    ...Array.from(removedIssueIds),
    ...Array.from(removedDraftIds),
    ...documents
      .filter((d) => d.workspaceId === LEGACY_SEED_WS_ID)
      .map((d) => d.id),
  ]);

  // Also collect document IDs that were removed (before filtering)
  const removedDocumentIds = new Set(
    state.documents
      .filter(
        (doc) =>
          doc.workspaceId === LEGACY_SEED_WS_ID ||
          removedSessionIds.has(doc.plenarySessionId ?? '')
      )
      .map((doc) => doc.id)
  );

  const allRemovedEntityIds = new Set([
    ...removedSessionIds,
    ...removedAgendaItemIds,
    ...Array.from(removedIssueIds),
    ...Array.from(removedDraftIds),
    ...Array.from(removedDocumentIds),
  ]);

  function filterNotifications(items: DemoNotification[]): DemoNotification[] {
    return items.filter((n) => {
      // Remove if explicitly tagged to the legacy workspace
      if (n.workspaceId === LEGACY_SEED_WS_ID) return false;
      // Remove if entityId references a deleted entity
      if (n.entityId && allRemovedEntityIds.has(n.entityId)) return false;
      return true;
    });
  }

  const alerts = filterNotifications(state.alerts);
  const notifications = filterNotifications(state.notifications);

  // 5. Clean up internalNotes: remove entries whose key references a deleted entityId.
  //    Key format is typically "entityType:entityId" or just "entityId".
  //    We remove any key that contains a deleted entity ID.
  const internalNotes: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(state.internalNotes)) {
    // Check if the key contains any removed entity ID
    const isDeleted = Array.from(allRemovedEntityIds).some(
      (id) => key === id || key.endsWith(`:${id}`) || key.startsWith(`${id}:`)
    );
    if (!isDeleted) {
      internalNotes[key] = value;
    }
  }

  // 6. Normalise legacy demo profile
  const userProfiles = state.userProfiles.map((p) => {
    const isLegacyProfile =
      (p.nom === 'Montserrat' && (p.cognoms ?? '').toLowerCase().includes('vidal')) ||
      (p.email === 'mvidal@grupindependent-berga.cat');
    if (!isLegacyProfile) return p;
    return {
      ...p,
      nom: 'Marta',
      cognoms: 'Serra',
      email: 'demo@plenari.cat',
      avatarInicials: 'MS',
    };
  });

  return {
    ...state,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    workspaces,
    workspaceMemberships,
    activeWorkspaceId,
    userProfiles,
    plenarySessions,
    agendaItems,
    documents,
    issues,
    drafts,
    suggestedQuestions,
    activityEvents,
    alerts,
    notifications,
    internalNotes,
    metadata: {
      ...state.metadata,
      lastModifiedAt: new Date().toISOString(),
      seedVersion: '3.0.0',
    },
  };
}

// ─── Public API ────────────────────────────────────────────────────────

/**
 * Migrates a raw parsed object to the current DemoState schema.
 * Creates a backup before any destructive recovery.
 *
 * Migration paths:
 *   - No state / corrupt → seed (fresh start)
 *   - version 0 / missing → migrateToV1SchemaV2 → applySchemaV3
 *   - version 1, schemaVersion < 2 → migrateToV1SchemaV2 → applySchemaV3
 *   - version 1, schemaVersion 2 → applySchemaV3 only
 *   - version 1, schemaVersion 3 → normalise only (idempotent)
 *   - Unknown version → backup + seed
 *
 * What happens when a browser with pre-PR18 state opens the new version:
 *   1. localStorage key 'plenaricat_demo_state_v1' is read
 *   2. If version === 0 or missing: full migration runs, workspaceId backfilled
 *      on sessions/issues/drafts/notifications using LEGACY_WORKSPACE_MAP
 *      (muni-001 → ws-muni-001-general, muni-002 → ws-muni-002, etc.)
 *   3. schemaV3 migration removes ws-muni-001-general seed workspace
 *   4. OrganizationMunicipalityConfigs are merged: persisted configs preserved,
 *      missing seed configs added
 *   5. No data loss: all persisted arrays are preserved; seed only fills gaps
 *   6. A backup is created before any destructive operation
 *
 * What happens when a browser with PR#18 state (schemaV2) opens PR#19:
 *   1. schemaV3 migration runs: removes ws-muni-001-general seed workspace
 *   2. Legacy profile normalised if present
 *   3. activeWorkspaceId cleared if it was ws-muni-001-general
 */
export function migrateDemoState(raw: unknown): DemoState {
  if (!isObject(raw)) {
    // Corrupt or empty — backup and return seed
    backupCurrentState();
    return createInitialDemoState();
  }

  const version = typeof raw['version'] === 'number' ? raw['version'] : 0;
  const schemaVersion = typeof raw['schemaVersion'] === 'number' ? raw['schemaVersion'] : 0;

  // Already current version and schema — still run normalisation to fix inconsistencies
  if (version === CURRENT_VERSION && schemaVersion === CURRENT_SCHEMA_VERSION) {
    return applySchemaV3(migrateToV1SchemaV2(raw));
  }

  // Version 1, schemaVersion 2 (PR#18 QA) → apply schemaV3 only
  if (version === 1 && schemaVersion === 2) {
    return applySchemaV3(migrateToV1SchemaV2(raw));
  }

  // Version 0 or missing version → full migration to v1/schemaV2 then schemaV3
  if (version === 0 || !raw['version']) {
    return applySchemaV3(migrateToV1SchemaV2(raw));
  }

  // Version 1, schemaVersion < 2 (pre-PR18 QA) → migrate to schemaV2 then schemaV3
  if (version === 1 && schemaVersion < CURRENT_SCHEMA_VERSION) {
    return applySchemaV3(migrateToV1SchemaV2(raw));
  }

  // Unknown version — backup and return seed
  backupCurrentState();
  return createInitialDemoState();
}
